import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type LandingCurrencyCode = 'EUR' | 'GBP' | 'USD';

export interface LandingCurrency {
  code: LandingCurrencyCode;
  symbol: string;
}

const CURRENCIES: LandingCurrency[] = [
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'USD', symbol: '$' },
];

const STORAGE_KEY = 'icip_landing_currency';

// EUR is the default for first-time visitors — CURRENCIES[0] drives both the fallback below
// and the dropdown's display order.
const DEFAULT_CURRENCY: LandingCurrency = CURRENCIES[0];

// Shared currency selection for the pre-login public site (navbar toggle + pricing section).
// Mirrors PlanPromptService's BehaviorSubject pattern.
@Injectable({ providedIn: 'root' })
export class CurrencyService {
  readonly currencies = CURRENCIES;

  private currencySubject = new BehaviorSubject<LandingCurrency>(this.readStored());
  currency$ = this.currencySubject.asObservable();

  get currency(): LandingCurrency {
    return this.currencySubject.getValue();
  }

  select(code: LandingCurrencyCode): void {
    if (code === this.currency.code) return;
    const match = CURRENCIES.find((c) => c.code === code);
    if (!match) return;
    this.currencySubject.next(match);
    try {
      localStorage.setItem(STORAGE_KEY, match.code);
    } catch {
      // ignore storage errors (private browsing, etc.)
    }
  }

  private readStored(): LandingCurrency {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const match = CURRENCIES.find((c) => c.code === stored);
      if (match) return match;
    } catch {
      // ignore storage errors
    }
    return DEFAULT_CURRENCY;
  }
}
